import React, {useCallback, useMemo, useState} from "react";
import {Box, DialogActions, DialogContent, TextField, Tooltip, Typography} from "@mui/material";
import Button from "../../../../../../../components/base/button/button";
import useIsMounted from "../../../../../../../hooks/use-is-mounted";
import LoadingLottieAnimation from "../../../../../../../components/app-specific/lottie-animations/loading";
import ToastService from "../../../../../../../../core/services/toast";
import {DataGridColumnTypes, DataGridColumnWidthTypes, useDataGridSavedState} from "../../../../../../../../packages/data-grid";
import {StandaloneCheckbox} from "../../../../../../../components/base/checkbox/checkbox";
import PartnerDataGrid from "../../../../../../../containers/data-grid";
import Utils from "../../../../../../../../core/services/utils/utils";
import {Tab, Tabs} from "../../../../../../../components/base/Tabs";
import ProductInformationPopover from "../../../../../../../components/app-specific/draggable-popover/product-information-popover";
import Api from "../../../../../../../../core/services/api/api";


const ProductVarianceImportDialogPrefixImport = (props) => {
    const {onImport, closeDialog} = props;
    const [newPrefix, setNewPrefix] = useState('');
    const [prefixList, setPrefixList] = useState([]);
    const [selectedPrefixId, setSelectedPrefixId] = useState();
    const isMounted = useIsMounted();

    const anyPrefixImporting = useMemo(() => prefixList.some(e => e.importing), [prefixList]);
    const selectedPrefix = useMemo(() => prefixList.find(e => e.id === selectedPrefixId), [prefixList, selectedPrefixId]);
    const setSelectedPrefix = useCallback((setState) => {
        setPrefixList((prev) => prev.map(e => e.id === selectedPrefixId ? {
            ...e,
            ...(typeof setState === 'function' ? setState(e) : setState),
        } : e));
    }, [selectedPrefixId]);


    /**
     * Fetch products based on the newPrefix entered by the user.
     */
    const fetchProductsViaPrefix = useCallback(async () => {
        const prefixTrimmed = newPrefix.trim().toUpperCase();
        if (prefixTrimmed.length === 0) {
            ToastService.error({message: "Prefix cannot be empty.", title: "Invalid Prefix"});
            return;
        }
        const prefixData = {
            id: Utils.createUUID(),
            prefix: prefixTrimmed,
            products: [],
            selection: [],
            importing: true,
        }
        setPrefixList(prevState => [...prevState, prefixData])
        const response = await Api.loadPartsByPrefix(prefixData.prefix);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setPrefixList(prevState => prevState.map(e => e.id === prefixData.id ? {
                ...e,
                importing: false,
                products: response.data?.map(e => ({...e})) ?? [],
                selection: response.data?.map(e => e.id) ?? [],
            } : e));
            setSelectedPrefixId(prefixData.id);
            setNewPrefix('');
        } else {
            setPrefixList(prevState => prevState.filter(e => e.id !== prefixData.id));
        }
    }, [isMounted, newPrefix])

    /**
     * Callback function passed to the column mapper.
     */
    const importSelection = useCallback(() => {
        const selectedProducts = selectedPrefix?.products
            .filter(product => selectedPrefix?.selection.includes(product.id) ?? false)
            .map(e => ({...e})) ?? []
        if (selectedProducts.length === 0) {
            ToastService.error({message: "Please select at least one product.", title: "No Products Selected"});
            return;
        }
        closeDialog?.();
        onImport?.(selectedProducts);
    }, [closeDialog, onImport, selectedPrefix?.products, selectedPrefix?.selection])

    /**
     * Export the current selection as a CSV file.
     */
    const exportSelection = useCallback(() => {
        const selectedProducts = selectedPrefix?.products
            .filter(product => selectedPrefix?.selection.includes(product.id) ?? false)
            .map(e => ({...e})) ?? []
        if (selectedProducts.length === 0) {
            ToastService.error({message: "Please select at least one product.", title: "No Products Selected"});
            return;
        }
        const csvContent = selectedProducts.map(e => ({
            "Part No": e.partNo,
        }));
        Utils.exportCsvFile(csvContent, {fileTitle: `product-variance-export-prefix-${selectedPrefix?.prefix ?? 'selection'}`});
    }, [selectedPrefix?.prefix, selectedPrefix?.products, selectedPrefix?.selection])

    /**
     * If the user presses enter, and the new option is enabled, then add the next option.
     */
    const onFormKeyDown = useCallback((e) => {
        if (e.key !== 'Enter') {
            return;
        }
        void fetchProductsViaPrefix();
    }, [fetchProductsViaPrefix])

    return (
        <>
            <DialogContent>
                <Box sx={{display: 'flex', alignItems: 'center', flexDirection: 'column', width: '100%', gap: 2}}>
                    <TextField
                        fullWidth
                        label={'Prefix'}
                        size={'small'}
                        placeholder={'Enter Prefix to search products'}
                        value={newPrefix}
                        onChange={(e) => setNewPrefix(e.target.value?.toUpperCase() ?? "")}
                        disabled={anyPrefixImporting}
                        onKeyDown={onFormKeyDown}
                    />
                    {
                        prefixList.length > 0 &&
                        <Tabs className={'d-flex w-100'} value={selectedPrefixId ?? false} scrollButtons={false} onChange={(_, newValue) => setSelectedPrefixId(newValue)}>
                            {
                                prefixList.map((prefix) => (
                                    <Tab
                                        key={prefix.id}
                                        value={prefix.id}
                                        label={`${prefix.prefix} ${prefix.importing ? '(loading...)' : `(${prefix.products.length} products)`}`}
                                    />
                                ))
                            }
                        </Tabs>
                    }
                    {
                        !selectedPrefix &&
                        <Typography variant={'body2'} fontWeight={500} sx={{marginBlock: 4}}>
                            Please enter a prefix to search for products.
                        </Typography>
                    }
                    {
                        selectedPrefix &&
                        <PrefixInformation
                            data={selectedPrefix}
                            onChange={setSelectedPrefix}
                        />
                    }
                </Box>
            </DialogContent>
            <DialogActions sx={{display: 'flex', alignItems: 'center', p: 0, m: 0}}>
                <Button
                    variant={'dialog'}
                    size={'medium'}
                    type={'button'}
                    color={'primary'}
                    borderRadius={'0 8px 0 8px'}
                    onClick={props.closeDialog}
                >
                    Close
                </Button>
                <div className={'ml-auto'}/>
                {
                    prefixList.length > 0 && !!selectedPrefix && selectedPrefix.selection.length > 0 &&
                    <Tooltip title={`Imports the currently selected prefix's selection (${selectedPrefix.prefix}). If you want to import other prefixes, please select them from the tabs above.`}>
                        <Button
                            variant={'dialog'}
                            size={'medium'}
                            color={'primary'}
                            type={'submit'}
                            borderRadius={'8px 0 8px 0'}
                            onClick={importSelection}
                            disabled={selectedPrefix?.selection.length === 0}
                        >
                            Import Loaded Products ({selectedPrefix?.selection.length} parts)
                        </Button>
                    </Tooltip>
                }
                {
                    prefixList.length > 0 && !!selectedPrefix && selectedPrefix.selection.length > 0 &&
                    <Tooltip
                        title={`Exports the currently selected prefix's selection (${selectedPrefix.prefix}) as a CSV file. If you want to export other prefixes, please select them from the tabs above.`}>
                        <Button
                            variant={'dialog'}
                            size={'medium'}
                            color={'primary'}
                            type={'submit'}
                            borderRadius={'8px 0 8px 0'}
                            onClick={exportSelection}
                            disabled={selectedPrefix?.selection.length === 0}
                        >
                            Export as CSV ({selectedPrefix?.selection.length} parts)
                        </Button>
                    </Tooltip>
                }
            </DialogActions>
        </>
    );
}


const dataGridColumnsNames = {
    partNo1: 'partNo1',
    partNo2: 'partNo2',
    partNo3: 'partNo3',
}

const dataGridSaveConfig = {
    name: 'ProductVarianceImportWithPrefixDG',
    version: 1,
}

const PrefixInformation = (props) => {
    const {onChange: onChangeProps} = props;

    const products = useMemo(() => props.data.products, [props.data.products]);

    const selection = useMemo(() => props.data.selection, [props.data.selection]);
    const setSelection = useCallback((setState) => {
        onChangeProps((prev) => ({
            ...prev,
            selection: typeof setState === 'function' ? setState(prev.selection) : setState,
        }));
    }, [onChangeProps])

    /**@type {DataGridColumn[]}*/
    const columns = useMemo(() => [
        {
            name: dataGridColumnsNames.partNo1,
            title: 'Part No',
            type: DataGridColumnTypes.string,
            width: {size: 1, type: DataGridColumnWidthTypes.flex},
            sortable: false,
        },
        {
            name: dataGridColumnsNames.partNo2,
            title: 'Part No',
            type: DataGridColumnTypes.string,
            width: {size: 1, type: DataGridColumnWidthTypes.flex},
            sortable: false,
        },
        {
            name: dataGridColumnsNames.partNo3,
            title: 'Part No',
            type: DataGridColumnTypes.string,
            width: {size: 1, type: DataGridColumnWidthTypes.flex},
            sortable: false,
        },
    ], [])

    const [dataGridColumns, , , dataGridDensity] = useDataGridSavedState({
        columns: columns,
        version: dataGridSaveConfig.version,
        name: dataGridSaveConfig.name,
    });

    /**
     * Change the selection of a product.
     */
    const changeSelection = useCallback((productId, checked) => {
        setSelection(prev => {
            if (checked) {
                return [...prev, productId];
            } else {
                return prev.filter(id => id !== productId);
            }
        })
    }, [setSelection]);

    const dataGridRows = useMemo(() => {
        const resultsInThree = products.reduce((resultArray, item, index) => {
            const chunkIndex = Math.floor(index / 3);
            if (!resultArray[chunkIndex]) {
                resultArray[chunkIndex] = []; // start a new chunk
            }

            resultArray[chunkIndex].push(item);
            return resultArray;
        }, []);
        return resultsInThree.map(([product1, product2, product3]) => ({
            key: `${product1.id}-${product2?.id ?? 'nopart'}-${product3?.id ?? 'nopart'}`,
            data: [product1, product2, product3],
            cells: {
                [dataGridColumnsNames.partNo1]: !!product1 ?
                    <React.Fragment>
                        <StandaloneCheckbox
                            checked={selection.includes(product1.id)}
                            onChange={(_, checked) => changeSelection(product1.id, checked)}
                        />
                        <ProductInformationPopover partNo={product1.partNo}>
                            {product1.partNo ?? ''}
                        </ProductInformationPopover>
                    </React.Fragment> : " ",
                [dataGridColumnsNames.partNo2]: !!product2 ?
                    <React.Fragment>
                        <StandaloneCheckbox
                            checked={selection.includes(product2?.id ?? 'nopart')}
                            onChange={(_, checked) => changeSelection(product2.id, checked)}
                        />
                        <ProductInformationPopover partNo={product2.partNo}>
                            {product2?.partNo ?? ''}
                        </ProductInformationPopover>
                    </React.Fragment> : " ",
                [dataGridColumnsNames.partNo3]: !!product3 ?
                    <React.Fragment>
                        <StandaloneCheckbox
                            checked={selection.includes(product3.id ?? 'nopart')}
                            onChange={(_, checked) => changeSelection(product3.id, checked)}
                        />
                        <ProductInformationPopover partNo={product3.partNo}>
                            {product3.partNo ?? ''}
                        </ProductInformationPopover>
                    </React.Fragment> : " "
            },
        }));
    }, [products, selection, changeSelection])

    return (
        <>
            {
                props.data.importing &&
                <>
                    <LoadingLottieAnimation width={'80%'} className={''}/>
                </>
            }
            {
                !props.data.importing &&
                <>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1, width: '100%'}}>
                        <Typography variant={'body2'} marginBlock={2}>
                            Select Products for: {props.data.prefix}. Total products loaded: {products.length}. please note that you can import these selected products by clicking the "Import Loaded
                            Products" button below or export them as a CSV file by clicking the "Export as CSV" button.
                        </Typography>
                    </Box>
                    <PartnerDataGrid
                        classNames={{toolbar: "pt-0", layout: 'mt-0'}}
                        visibleRows={5}
                        {...dataGridSaveConfig}
                        columns={dataGridColumns}
                        initialLayoutState={{columns: columns}}
                        rows={dataGridRows}
                        useVirtualizedRows
                        hideToolbar
                        state={{
                            pagination: {pageSize: products.length, currentPage: 1, length: products.length},
                            loading: {state: false},
                            density: dataGridDensity,
                        }}
                        hideFooter
                        hideExporting
                    />
                </>
            }
        </>
    );
}

export default ProductVarianceImportDialogPrefixImport;